/**
 * Candidate Analysis Types
 * Feature: 001-structured-candidate-scoring
 * 
 * Defines TypeScript interfaces and Zod schemas for multi-dimensional
 * candidate scoring system with 4 evaluation modules.
 */

import { z } from 'zod';

// ============================================================================
// Zod Schemas (Runtime Validation + Type Inference)
// ============================================================================

/**
 * Schema for Relevant Experience module explanation
 */
export const RelevantExperienceDetailsSchema = z.object({
  bullets: z.array(z.string()).length(2).describe('Two bullet points explaining the experience score'),
});

/**
 * Schema for Job Stability module explanation
 */
export const StabilityDetailsSchema = z.object({
  explanation: z.string().describe('Explanation of employment stability patterns'),
});

/**
 * Schema for Qualifications module explanation
 */
export const QualificationsDetailsSchema = z.object({
  found: z.array(z.string()).describe('Certifications/qualifications that match job requirements'),
  missing: z.array(z.string()).describe('Required certifications/qualifications not found'),
});

/**
 * Schema for Role Alignment module explanation
 */
export const RoleAlignmentDetailsSchema = z.object({
  justification: z.string().describe('One-sentence justification of role fit'),
});

/**
 * Complete score details schema (JSONB structure for database)
 */
export const ScoreDetailsSchema = z.object({
  relevant_experience: RelevantExperienceDetailsSchema,
  stability: StabilityDetailsSchema,
  qualifications: QualificationsDetailsSchema,
  role_alignment: RoleAlignmentDetailsSchema,
});

/**
 * Schema for candidate analysis request
 */
export const CandidateAnalysisRequestSchema = z.object({
  force_regenerate: z.boolean().optional().default(false).describe('If true, bypasses cache and generates fresh analysis'),
  model_version: z.string().optional().default('gpt-4o').describe('AI model to use for scoring'),
});

/**
 * Schema for candidate analysis response (full record)
 */
export const CandidateAnalysisSchema = z.object({
  id: z.string().uuid().describe('Unique identifier for this analysis'),
  application_id: z.string().uuid().describe('Foreign key to the job application'),
  
  // Individual module scores (0-100)
  relevant_experience_score: z.number().int().min(0).max(100).describe('Score (0-100) for relevant work experience'),
  stability_score: z.number().int().min(0).max(100).describe('Score (0-100) for employment stability'),
  qualifications_score: z.number().int().min(0).max(100).describe('Score (0-100) for qualifications/certifications match'),
  role_alignment_score: z.number().int().min(0).max(100).describe('Score (0-100) for role fit and career alignment'),
  final_weighted_score: z.number().int().min(0).max(100).describe('Weighted average of all 4 module scores'),
  
  // Structured explanations
  score_details: ScoreDetailsSchema.describe('Detailed explanations for each scoring module'),
  summary_comment: z.string().describe('AI-generated 1-sentence summary recommendation'),
  
  // Metadata
  generated_at: z.coerce.date().describe('Timestamp when analysis was generated'),
  generated_by: z.string().uuid().nullable().optional().describe('User ID who triggered analysis (null if automated)'),
  model_version: z.string().describe('AI model used for scoring'),
  processing_time_ms: z.number().int().nullable().optional().describe('Time taken to generate analysis (milliseconds)'),
});

/**
 * Schema for analysis job response (async processing)
 */
export const AnalysisJobResponseSchema = z.object({
  job_id: z.string().uuid().describe('Unique identifier for the background job'),
  status: z.enum(['queued', 'processing', 'completed', 'failed']).describe('Current status of the analysis job'),
  application_id: z.string().uuid().describe('Application being analyzed'),
  estimated_completion_time: z.coerce.date().optional().describe('Estimated completion time (typically 10 seconds from start)'),
  poll_url: z.string().describe('URL to poll for job status'),
});

/**
 * Schema for API error responses
 */
export const ErrorResponseSchema = z.object({
  error: z.string().describe('Error code'),
  message: z.string().describe('Human-readable error message'),
  details: z.record(z.unknown()).nullable().optional().describe('Additional error context'),
});

// ============================================================================
// TypeScript Types (Inferred from Zod Schemas)
// ============================================================================

export type RelevantExperienceDetails = z.infer<typeof RelevantExperienceDetailsSchema>;
export type StabilityDetails = z.infer<typeof StabilityDetailsSchema>;
export type QualificationsDetails = z.infer<typeof QualificationsDetailsSchema>;
export type RoleAlignmentDetails = z.infer<typeof RoleAlignmentDetailsSchema>;

export type ScoreDetails = z.infer<typeof ScoreDetailsSchema>;
export type CandidateAnalysis = z.infer<typeof CandidateAnalysisSchema>;
export type CandidateAnalysisRequest = z.infer<typeof CandidateAnalysisRequestSchema>;
export type AnalysisJobResponse = z.infer<typeof AnalysisJobResponseSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// ============================================================================
// Module-Specific Scoring Schemas (for OpenAI Function Calling)
// ============================================================================

/**
 * Schema for Experience Scorer AI response
 */
export const ExperienceScorerResponseSchema = z.object({
  score: z.number().int().min(0).max(100).describe('Relevant experience score (0-100)'),
  explanation_bullets: z.array(z.string()).length(2).describe('Two bullet points explaining the score'),
});

/**
 * Schema for Stability Scorer AI response
 */
export const StabilityScorerResponseSchema = z.object({
  score: z.number().int().min(0).max(100).describe('Job stability score (0-100)'),
  explanation: z.string().describe('Explanation of employment tenure patterns'),
});

/**
 * Schema for Qualifications Scorer AI response
 */
export const QualificationsScorerResponseSchema = z.object({
  score: z.number().int().min(0).max(100).describe('Qualifications match score (0-100)'),
  found: z.array(z.string()).describe('Certifications/qualifications found'),
  missing: z.array(z.string()).describe('Required certifications/qualifications missing'),
});

/**
 * Schema for Alignment Scorer AI response
 */
export const AlignmentScorerResponseSchema = z.object({
  score: z.number().int().min(0).max(100).describe('Role alignment score (0-100)'),
  justification: z.string().describe('One-sentence justification of fit'),
});

export type ExperienceScorerResponse = z.infer<typeof ExperienceScorerResponseSchema>;
export type StabilityScorerResponse = z.infer<typeof StabilityScorerResponseSchema>;
export type QualificationsScorerResponse = z.infer<typeof QualificationsScorerResponseSchema>;
export type AlignmentScorerResponse = z.infer<typeof AlignmentScorerResponseSchema>;

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Scoring module names (for iteration and mapping)
 */
export type ScoringModuleName = 'relevant_experience' | 'stability' | 'qualifications' | 'role_alignment';

/**
 * Individual scoring module result
 */
export interface ScoringModuleResult {
  module: ScoringModuleName;
  score: number;
  explanation: string | string[]; // String for most, array for experience bullets
  success: boolean;
  error?: string;
}

/**
 * Color coding thresholds for UI display
 */
export const SCORE_COLOR_THRESHOLDS = {
  HIGH: 70, // Green: >70
  MEDIUM: 40, // Yellow: 40-70
  // Red: <40
} as const;

/**
 * Get color category for a score
 */
export function getScoreColor(score: number): 'green' | 'yellow' | 'red' {
  if (score > SCORE_COLOR_THRESHOLDS.HIGH) return 'green';
  if (score >= SCORE_COLOR_THRESHOLDS.MEDIUM) return 'yellow';
  return 'red';
}

/**
 * Calculate final weighted score (arithmetic average of 4 modules, rounded)
 */
export function calculateFinalScore(scores: {
  experience: number;
  stability: number;
  qualifications: number;
  alignment: number;
}): number {
  const average = (scores.experience + scores.stability + scores.qualifications + scores.alignment) / 4;
  return Math.round(average);
}
